import { FitAddon } from "@xterm/addon-fit"
import { Terminal as XTerm } from "@xterm/xterm"
import { Base64 } from "js-base64"
import { MoreHorizontal, RotateCcw } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import "@xterm/xterm/css/xterm.css"
import { setTerminalKeyboardCapture } from "../electronAPIs/app"
import { useTerminalTheme } from "../hooks/useTerminalTheme"
import { PtyHandle, type PtyOutputEvent } from "../nestAPIs/pty"
import type { RuntimeWorkspaceContext } from "../nestAPIs/runtimeContext"
import { DEFAULT_TERMINAL_THEME } from "../themes/terminalThemes"

interface TerminalInstance {
    terminal: XTerm
    fitAddon: FitAddon
    ptyHandle: PtyHandle | null
    bindingKey: string
    containerEl: HTMLDivElement | null
    lastEscapeTime: number
}

const DOUBLE_ESCAPE_THRESHOLD_MS = 300

// Global map of terminal instances keyed by ptyId
const terminalInstances = new Map<string, TerminalInstance>()

interface TerminalProps {
    ptyId: string
    cwd: string
    context: RuntimeWorkspaceContext
    className?: string
    onClose?: () => void
    compact?: boolean
}

export function Terminal({ ptyId, cwd, context, className, onClose, compact = false }: TerminalProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    // Get terminal theme from CSS variable (updates when UI theme changes)
    const terminalTheme = useTerminalTheme(containerRef)
    const runtimeContext = useMemo<RuntimeWorkspaceContext>(
        () => ({
            workspaceId: context.workspaceId,
            workspaceRoot: context.workspaceRoot,
            ...(context.worktreeId ? { worktreeId: context.worktreeId } : {}),
        }),
        [context.workspaceId, context.workspaceRoot, context.worktreeId]
    )
    const bindingKey = `${cwd}\0${runtimeContext.workspaceId}\0${runtimeContext.workspaceRoot}\0${runtimeContext.worktreeId ?? ""}`

    const handleRestart = useCallback(async () => {
        const instance = terminalInstances.get(ptyId)
        if (!instance) return
        instance.bindingKey = bindingKey

        // Kill existing PTY and wait for exit event to avoid race condition
        // where the old PTY's exit event marks the new handle as exited
        if (instance.ptyHandle) {
            const oldHandle = instance.ptyHandle
            instance.ptyHandle = null

            if (!oldHandle.exited) {
                await new Promise<void>((resolve) => {
                    oldHandle.on("exit", () => resolve())
                    oldHandle.kill()
                })
            } else {
                oldHandle.cleanup()
            }
        }
        if (terminalInstances.get(ptyId) !== instance || instance.bindingKey !== bindingKey) return

        // Clear terminal
        instance.terminal.clear()

        // Get current dimensions
        const cols = instance.terminal.cols
        const rows = instance.terminal.rows

        // Spawn new PTY
        const handle = await PtyHandle.spawn({ ptyId, cwd, cols, rows, context: runtimeContext })
        if (terminalInstances.get(ptyId) !== instance || instance.bindingKey !== bindingKey) {
            if (handle && !handle.exited) void handle.kill().catch(() => handle.cleanup())
            else handle?.cleanup()
            return
        }
        if (handle) {
            instance.ptyHandle = handle

            handle.on("output", (chunk: unknown) => {
                const { data } = chunk as PtyOutputEvent
                const decoded = Base64.decode(data)
                instance.terminal.write(decoded)
            })

            handle.on("exit", () => {
                instance.terminal.write("\r\n[Process exited]\r\n")
            })
        }
    }, [ptyId, cwd, runtimeContext, bindingKey])

    useEffect(() => {
        let mounted = true
        const container = containerRef.current
        if (!container) return

        let resizeObserver: ResizeObserver | null = null
        const handleFocusIn = () => {
            setTerminalKeyboardCapture(true).catch(() => {})
        }
        const handleFocusOut = (event: FocusEvent) => {
            const nextTarget = event.relatedTarget
            if (nextTarget instanceof Node && container.contains(nextTarget)) return
            setTerminalKeyboardCapture(false).catch(() => {})
        }

        container.addEventListener("focusin", handleFocusIn)
        container.addEventListener("focusout", handleFocusOut)

        async function setup() {
            if (!mounted || !container) return

            // Check if we already have an instance
            let instance = terminalInstances.get(ptyId)

            if (instance) {
                // Re-mount existing terminal to this container
                if (instance.containerEl !== container) {
                    // Move terminal to new container
                    container.innerHTML = ""
                    const termElement = instance.terminal.element
                    if (termElement) {
                        container.appendChild(termElement)
                    }
                    instance.containerEl = container
                }

                // Set up resize observer for the new container
                resizeObserver = new ResizeObserver(() => {
                    instance!.fitAddon.fit()
                })
                resizeObserver.observe(container)

                // Re-fit to new container dimensions
                instance.fitAddon.fit()
                if (instance.bindingKey !== bindingKey) {
                    await handleRestart()
                }
                // Focus terminal when opened
                instance.terminal.focus()
                return
            }

            // Create new terminal instance
            const terminal = new XTerm({
                cursorBlink: true,
                cursorStyle: "bar",
                fontSize: 13,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                theme: DEFAULT_TERMINAL_THEME,
            })

            const fitAddon = new FitAddon()
            terminal.loadAddon(fitAddon)
            terminal.open(container)
            fitAddon.fit()

            instance = {
                terminal,
                fitAddon,
                ptyHandle: null,
                bindingKey,
                containerEl: container,
                lastEscapeTime: 0,
            }
            terminalInstances.set(ptyId, instance)

            // Send user input to PTY
            terminal.onData((data) => {
                const inst = terminalInstances.get(ptyId)
                inst?.ptyHandle?.write(data)
            })

            // Double-Escape to unfocus terminal and close tray
            // In xterm.js: return true = process key normally, return false = prevent key
            terminal.attachCustomKeyEventHandler((event: KeyboardEvent) => {
                const isMac = navigator.platform.toUpperCase().includes("MAC")
                if (event.key === "Escape" || event.ctrlKey || event.altKey || (!isMac && event.metaKey)) {
                    event.stopPropagation()
                }

                if (event.key === "Escape" && event.type === "keydown") {
                    const now = Date.now()
                    const inst = terminalInstances.get(ptyId)
                    if (inst && now - inst.lastEscapeTime < DOUBLE_ESCAPE_THRESHOLD_MS) {
                        terminal.blur()
                        onCloseRef.current?.()
                        inst.lastEscapeTime = 0
                        return false // Prevent this escape from going to terminal
                    }
                    if (inst) {
                        inst.lastEscapeTime = now
                    }
                }
                return true // Allow all other keys to be processed normally
            })

            // Try to reconnect to existing PTY
            const { handle, found } = await PtyHandle.reconnect(ptyId, runtimeContext)
            if (!mounted || terminalInstances.get(ptyId) !== instance || instance.bindingKey !== bindingKey) {
                if (handle && !handle.exited) void handle.kill().catch(() => handle.cleanup())
                else handle?.cleanup()
                return
            }

            if (found && handle) {
                instance.ptyHandle = handle

                handle.on("output", (chunk: unknown) => {
                    const { data } = chunk as PtyOutputEvent
                    const decoded = Base64.decode(data)
                    terminal.write(decoded)
                })

                handle.on("exit", () => {
                    terminal.write("\r\n[Process exited]\r\n")
                })
            } else {
                // Spawn new PTY with fitted dimensions
                const newHandle = await PtyHandle.spawn({ ptyId, cwd, cols: terminal.cols, rows: terminal.rows, context: runtimeContext })
                if (!mounted || terminalInstances.get(ptyId) !== instance || instance.bindingKey !== bindingKey) {
                    if (newHandle && !newHandle.exited) void newHandle.kill().catch(() => newHandle.cleanup())
                    else newHandle?.cleanup()
                    return
                }
                if (newHandle) {
                    instance.ptyHandle = newHandle

                    newHandle.on("output", (chunk: unknown) => {
                        const { data } = chunk as PtyOutputEvent
                        const decoded = Base64.decode(data)
                        terminal.write(decoded)
                    })

                    newHandle.on("exit", () => {
                        terminal.write("\r\n[Process exited]\r\n")
                    })
                }
            }

            // Sync PTY dimensions when terminal resizes
            terminal.onResize(({ cols, rows }) => {
                const inst = terminalInstances.get(ptyId)
                inst?.ptyHandle?.resize(cols, rows)
            })

            // Set up resize observer for fit addon
            resizeObserver = new ResizeObserver(() => {
                fitAddon.fit()
            })
            resizeObserver.observe(container)

            // Focus terminal when opened
            terminal.focus()
        }

        setup()

        return () => {
            mounted = false
            resizeObserver?.disconnect()
            container.removeEventListener("focusin", handleFocusIn)
            container.removeEventListener("focusout", handleFocusOut)
            setTerminalKeyboardCapture(false).catch(() => {})
        }
    }, [ptyId, cwd, runtimeContext, bindingKey, handleRestart])

    // Update terminal theme when UI theme changes
    useEffect(() => {
        const instance = terminalInstances.get(ptyId)
        if (instance) {
            instance.terminal.options.theme = terminalTheme
        }
    }, [ptyId, terminalTheme])

    const sendKey = useCallback(
        (data: string) => {
            const instance = terminalInstances.get(ptyId)
            instance?.ptyHandle?.write(data)
            instance?.terminal.focus()
        },
        [ptyId]
    )

    return (
        <div className={twMerge("flex flex-col h-full bg-base-100", className)}>
            <div className={twMerge("items-center justify-between border-b border-border bg-base-200", compact ? "flex min-h-12 px-3" : "flex px-3 py-2")}>
                <span className="text-xs text-muted font-medium flex items-center gap-1.5">
                    {compact ? (
                        <span className="max-w-[70vw] truncate font-mono">{cwd}</span>
                    ) : (
                        <>
                            Terminal
                            <span className="text-muted/50">·</span>
                            <span className="text-muted/50 flex items-center gap-0.5">
                                <kbd className="px-1 bg-base-300 text-[10px]">esc</kbd>
                                <kbd className="px-1 bg-base-300 text-[10px]">esc</kbd>
                                to close
                            </span>
                        </>
                    )}
                </span>
                {compact ? (
                    <button
                        type="button"
                        onClick={() => setOptionsOpen((value) => !value)}
                        className="btn flex h-10 w-10 items-center justify-center text-muted hover:bg-base-300 hover:text-base-content"
                        aria-label="Terminal options"
                        aria-expanded={optionsOpen}
                    >
                        <MoreHorizontal size={18} />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleRestart}
                        className="btn flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-muted hover:text-base-content transition-colors"
                        title="Restart terminal"
                    >
                        <RotateCcw size="1em" />
                        Restart
                    </button>
                )}
            </div>
            {compact && optionsOpen && (
                <div className="flex shrink-0 border-b border-border bg-base-200 px-3 py-2">
                    <button
                        type="button"
                        onClick={() => {
                            void handleRestart()
                            setOptionsOpen(false)
                        }}
                        className="btn flex min-h-10 items-center gap-2 px-3 text-sm text-base-content hover:bg-base-300"
                    >
                        <RotateCcw size={15} />
                        Restart terminal
                    </button>
                </div>
            )}
            <div className="flex-1 min-h-0 px-2 pt-2" style={{ backgroundColor: terminalTheme.background }}>
                <div ref={containerRef} className="h-full" />
            </div>
            {compact && (
                <div className="grid shrink-0 grid-cols-7 border-t border-border bg-base-200">
                    {[
                        ["esc", "\u001b"],
                        ["tab", "\t"],
                        ["^C", "\u0003"],
                        ["←", "\u001b[D"],
                        ["↑", "\u001b[A"],
                        ["↓", "\u001b[B"],
                        ["→", "\u001b[C"],
                    ].map(([label, data]) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => sendKey(data)}
                            className="btn min-h-11 border-r border-border font-mono text-xs text-muted last:border-r-0 hover:bg-base-300 hover:text-base-content"
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
